import React, { useState, useRef, useEffect } from "react";
import {
  Box, Typography, TextField, IconButton, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, TableFooter, Chip, Collapse,
  CircularProgress, Tooltip, Avatar, Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement, Title, Tooltip as ChartTooltip, Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement, Title, ChartTooltip, Legend
);

const today = new Date().toISOString().slice(0, 10);
const jan1   = `${new Date().getFullYear()}-01-01`;

const CHART_COLORS = [
  "#42a5f5","#66bb6a","#ffa726","#ef5350",
  "#ab47bc","#26c6da","#ff7043","#9ccc65",
  "#5c6bc0","#ec407a","#26a69a","#d4e157",
];

const EXAMPLES = [
  "penjualan per sopir per bulan",
  "laba terbesar per kendaraan",
  "penjualan dan laba per sopir urut berdasarkan laba",
  "top 10 customer",
  "penjualan per kota tujuan",
  "total kubik per vendor",
  "saldo per bank",
  "laba per cabang",
  "grafik pie pengeluaran per perkiraan",
  "penjualan bulanan",
  "total penjualan per jenis kendaraan",
  "top 5 vendor kubik",
  "pengeluaran per rekanan",
  "trend penjualan harian",
  "kendaraan free",
];

function renderChart(chartData) {
  if (!chartData) return null;
  const { type, labels, datasets } = chartData;
  const data = {
    labels,
    datasets: datasets.map((d, i) => ({
      label: d.label,
      data: d.data,
      backgroundColor: type === "pie" ? CHART_COLORS : CHART_COLORS[i % CHART_COLORS.length],
      borderColor: type === "line" ? CHART_COLORS[i % CHART_COLORS.length] : "rgba(0,0,0,0.1)",
      borderWidth: type === "line" ? 2 : 1,
      fill: false,
      tension: 0.3,
      pointRadius: 4,
    })),
  };
  const options = {
    responsive: true,
    plugins: { legend: { position: "top" } },
    scales: type === "pie" ? {} : {
      y: {
        ticks: {
          callback: (v) => Intl.NumberFormat("id-ID", { notation: "compact" }).format(v),
        },
      },
    },
  };
  if (type === "line") return <Line data={data} options={options} />;
  if (type === "pie")  return <Pie  data={data} options={options} />;
  return <Bar data={data} options={options} />;
}

const FROZEN_COL = {
  position: "sticky", left: 0, zIndex: 1,
  borderRight: "2px solid #e0d0f0",
};
const TOTAL_HEAD = { bgcolor: "#ede7f6", color: "#4a148c", fontWeight: "bold" };
const TOTAL_CELL = { bgcolor: "#f9f0ff", fontWeight: 700 };

function renderTable(res, isPivot = false) {
  if (!res?.rows?.length) return null;
  const cols = res.columns || Object.keys(res.rows[0]);
  return (
    <Paper sx={{ mt: 1.5, overflowX: "auto", borderRadius: 2 }}>
      <Table size="small" sx={{ tableLayout: "auto" }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            {cols.map((c, ci) => (
              <TableCell
                key={c}
                sx={{
                  fontWeight: "bold", whiteSpace: "nowrap", fontSize: 12,
                  ...(isPivot && ci === 0 && { ...FROZEN_COL, bgcolor: "#f0e6f8", zIndex: 2 }),
                  ...(c === "TOTAL" && TOTAL_HEAD),
                }}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {res.rows.map((r, idx) => (
            <TableRow key={idx} hover>
              {cols.map((c, ci) => (
                <TableCell
                  key={c}
                  sx={{
                    whiteSpace: "nowrap", fontSize: 12,
                    ...(isPivot && ci === 0 && {
                      ...FROZEN_COL,
                      bgcolor: idx % 2 === 0 ? "#fff" : "#faf5ff",
                      fontWeight: 600,
                    }),
                    ...(c === "TOTAL" && TOTAL_CELL),
                  }}
                >
                  {typeof r[c] === "number"
                    ? Intl.NumberFormat("id-ID").format(r[c])
                    : (r[c] ?? "-")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={cols.length} sx={{ borderBottom: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {res.rows.length} baris
                {res.period?.tgl1 && ` · Periode: ${res.period.tgl1} s/d ${res.period.tgl2}`}
              </Typography>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Paper>
  );
}

const PROVIDER_LABEL = {
  groq:     "AI · Groq",
  gemini:   "AI · Gemini",
  anthropic:"AI · Claude",
  ollama:   "AI · Ollama",
  pivot:    "Pivot",
  keyword:  "Kata kunci",
};

function ProviderBadge({ provider }) {
  const isAi = provider && provider !== "keyword";
  return (
    <Chip
      label={PROVIDER_LABEL[provider] || provider}
      size="small"
      sx={{
        height: 18, fontSize: 10, fontWeight: 600,
        bgcolor: isAi ? "#ede7f6" : "#f5f5f5",
        color:   isAi ? "#6a1b9a" : "#757575",
        border: "none", mb: 0.5,
      }}
    />
  );
}

function AiMessage({ msg }) {
  const [showTable, setShowTable] = useState(true);
  if (msg.error) {
    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
        <Avatar sx={{ bgcolor: "#9B459C", width: 32, height: 32 }}>
          <SmartToyIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box sx={{
          bgcolor: "#fdecea", color: "#c62828", borderRadius: "0 12px 12px 12px",
          px: 2, py: 1.5, maxWidth: "85%", fontSize: 14,
        }}>
          {msg.error}
        </Box>
      </Box>
    );
  }
  const res = msg.data;
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
      <Avatar sx={{ bgcolor: "#9B459C", width: 32, height: 32, flexShrink: 0 }}>
        <SmartToyIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ maxWidth: "85%", minWidth: 0 }}>
        {res.provider && <ProviderBadge provider={res.provider} />}
        {res.narasi && (
          <Box sx={{
            bgcolor: "#f3e8ff", borderRadius: "0 12px 12px 12px",
            px: 2, py: 1.5, fontSize: 14, whiteSpace: "pre-line", mb: 1,
            color: "#3d0066",
          }}>
            {res.narasi}
          </Box>
        )}
        {res.chart && !res.is_pivot && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, p: 1.5, border: "1px solid #e0e0e0", mb: 1 }}>
            {renderChart(res.chart)}
          </Box>
        )}
        {res.rows?.length > 0 && (
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 0.5, mb: 0.5 }}
              onClick={() => setShowTable((v) => !v)}
            >
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                {showTable ? "Sembunyikan tabel" : `Tampilkan tabel (${res.rows.length} baris)`}
              </Typography>
              {showTable ? <ExpandLessIcon sx={{ fontSize: 16, color: "primary.main" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "primary.main" }} />}
            </Box>
            <Collapse in={showTable}>
              {renderTable(res, res.is_pivot)}
            </Collapse>
          </Box>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5, pl: 0.5 }}>
          {msg.time}
        </Typography>
      </Box>
    </Box>
  );
}

function UserMessage({ msg }) {
  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "flex-start", mb: 2 }}>
      <Box sx={{ maxWidth: "75%" }}>
        {(msg.tgl1 || msg.tgl2) && (
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end",
            mb: 0.5,
          }}>
            <CalendarMonthIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            <Typography variant="caption" color="text.disabled">
              {msg.tgl1 || "..."} s/d {msg.tgl2 || "..."}
            </Typography>
          </Box>
        )}
        <Box sx={{
          bgcolor: "#9B459C", color: "#fff", borderRadius: "12px 0 12px 12px",
          px: 2, py: 1.5, fontSize: 14, wordBreak: "break-word",
        }}>
          {msg.text}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5, textAlign: "right", pr: 0.5 }}>
          {msg.time}
        </Typography>
      </Box>
      <Avatar sx={{ bgcolor: "#e0e0e0", width: 32, height: 32, flexShrink: 0 }}>
        <PersonIcon sx={{ fontSize: 18, color: "#555" }} />
      </Avatar>
    </Box>
  );
}

function TypingBubble() {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
      <Avatar sx={{ bgcolor: "#9B459C", width: 32, height: 32 }}>
        <SmartToyIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{
        bgcolor: "#f3e8ff", borderRadius: "0 12px 12px 12px",
        px: 2, py: 1.5, display: "flex", gap: 0.5, alignItems: "center",
      }}>
        <CircularProgress size={12} sx={{ color: "#9B459C" }} />
        <Typography variant="caption" color="#9B459C" sx={{ ml: 0.5 }}>
          Menganalisis data Oracle...
        </Typography>
      </Box>
    </Box>
  );
}

export default function AiPenjualan() {
  const [q, setQ]               = useState("");
  const [tgl1, setTgl1]         = useState(jan1);
  const [tgl2, setTgl2]         = useState(today);
  const [loading, setLoading]   = useState(false);
  const [history, setHistory]   = useState([]);
  const [showExamples, setShowExamples] = useState(false);
  const bottomRef = useRef();
  const inputRef  = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, loading]);

  const nowTime = () =>
    new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });

  const handleSend = async () => {
    const query = q.trim();
    if (!query || loading) return;

    const userMsg = { role: "user", text: query, tgl1, tgl2, time: nowTime() };
    setHistory((h) => [...h, userMsg]);
    setQ("");
    setLoading(true);

    try {
      const schema = localStorage.getItem("schema");

      // Compact history for context (last 6 items = 3 pairs), strip large row data
      const historyCtx = history.slice(-6).map((m) =>
        m.role === "user"
          ? { role: "user", text: m.text }
          : { role: "ai", sql: m.data?.sql || "", narasi: m.data?.narasi || "" }
      );

      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/ai_analisa.php?schema=${schema}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ q: query, tgl1, tgl2, history: historyCtx }),
        }
      );
      const json = await resp.json();

      if (!json.ok) throw new Error(json.error || "Gagal menganalisa data.");
      setHistory((h) => [...h, { role: "ai", data: json, time: nowTime() }]);
    } catch (e) {
      setHistory((h) => [...h, { role: "ai", error: e.message, time: nowTime() }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExample = (ex) => {
    setQ(ex);
    setShowExamples(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClear = () => {
    if (window.confirm("Hapus semua history chat?")) setHistory([]);
  };

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 72px)",
      bgcolor: "#f8f4ff",
    }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5, bgcolor: "#fff",
        borderBottom: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 1,
        flexShrink: 0,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "#9B459C", width: 36, height: 36 }}>
            <SmartToyIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              AI Analisis Bisnis
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <CalendarMonthIcon sx={{ fontSize: 18, color: "#9B459C" }} />
          <TextField
            label="Tanggal Awal" type="date" value={tgl1}
            onChange={(e) => setTgl1(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150, "& .MuiInputBase-input": { py: 1.8 } }}
          />
          <Typography variant="body2" color="text.secondary">s/d</Typography>
          <TextField
            label="Tanggal Akhir" type="date" value={tgl2}
            onChange={(e) => setTgl2(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150, "& .MuiInputBase-input": { py: 1.8 } }}
          />
          {history.length > 0 && (
            <Tooltip title="Hapus Semua Chat">
              <IconButton size="small" onClick={handleClear} sx={{ color: "#ef5350" }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Chat area */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
        {history.length === 0 && !loading && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <SmartToyIcon sx={{ fontSize: 56, color: "#d1b3e8", mb: 1 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
              AI siap membantu analisis bisnis Anda
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              Tanyakan tentang penjualan, keuangan, atau laba bersih
            </Typography>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.disabled">Contoh pertanyaan</Typography>
            </Divider>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "center", maxWidth: 600, mx: "auto" }}>
              {EXAMPLES.map((ex) => (
                <Chip key={ex} label={ex} size="small" variant="outlined"
                  onClick={() => handleExample(ex)}
                  sx={{ cursor: "pointer", borderColor: "#9B459C", color: "#9B459C",
                    "&:hover": { bgcolor: "#f3e8ff" } }}
                />
              ))}
            </Box>
          </Box>
        )}

        {history.map((msg, idx) =>
          msg.role === "user"
            ? <UserMessage key={idx} msg={msg} />
            : <AiMessage key={idx} msg={msg} />
        )}

        {loading && <TypingBubble />}
        <div ref={bottomRef} />
      </Box>

      {/* Input area */}
      <Box sx={{
        px: 2, py: 1.5, bgcolor: "#fff",
        borderTop: "1px solid #e0e0e0", flexShrink: 0,
      }}>
        {history.length > 0 && (
          <Collapse in={showExamples}>
            <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {EXAMPLES.map((ex) => (
                <Chip key={ex} label={ex} size="small" variant="outlined"
                  onClick={() => handleExample(ex)}
                  sx={{ cursor: "pointer", fontSize: 11,
                    borderColor: "#9B459C", color: "#9B459C",
                    "&:hover": { bgcolor: "#f3e8ff" } }}
                />
              ))}
            </Box>
          </Collapse>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              inputRef={inputRef}
              placeholder="Tanyakan seputar penjualan, keuangan, laba bersih..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              multiline
              maxRows={4}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#fafafa",
                },
              }}
            />
            {history.length > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, cursor: "pointer", width: "fit-content" }}
                onClick={() => setShowExamples((v) => !v)}
              >
                <Typography variant="caption" color="#9B459C" sx={{ fontSize: 11 }}>
                  {showExamples ? "Sembunyikan contoh" : "Lihat contoh"}
                </Typography>
                {showExamples
                  ? <ExpandLessIcon sx={{ fontSize: 13, color: "#9B459C" }} />
                  : <ExpandMoreIcon sx={{ fontSize: 13, color: "#9B459C" }} />}
              </Box>
            )}
          </Box>
          <IconButton
            onClick={handleSend}
            disabled={loading || !q.trim()}
            sx={{
              bgcolor: "#9B459C", color: "#fff", mb: history.length > 0 ? 2.5 : 0,
              "&:hover": { bgcolor: "#7b3080" },
              "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#bbb" },
              width: 44, height: 44, flexShrink: 0,
            }}
          >
            {loading
              ? <CircularProgress size={20} sx={{ color: "#fff" }} />
              : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
